/** Batch-submit round: six slots, pool, submit scoring, clear-on-fail lives. */

import type { HistoryEvent } from './pool.js';
import { CARDS_PER_DAY } from './config.js';
import type { ScheduledDay } from './schedule.js';

export const MAX_LIVES = 4;
export const SLOT_COUNT = CARDS_PER_DAY;
export const YEAR_FLIP_MS = 300;

export type RoundOutcome = 'win' | 'loss' | null;

export type RoundState = {
  day: number;
  pool: HistoryEvent[];
  slots: Array<HistoryEvent | null>;
  lives: number;
  ended: boolean;
  outcome: RoundOutcome;
  /** Successful or failed submits attempted this round. */
  attempts: number;
  lastCorrectCount: number | null;
};

export type SubmitResult = {
  state: RoundState;
  correctCount: number;
  won: boolean;
};

export function yearFlipDurationMs(prefersReducedMotion: boolean): number {
  return prefersReducedMotion ? 0 : YEAR_FLIP_MS;
}

export function trueOrder(cards: HistoryEvent[]): HistoryEvent[] {
  return [...cards].sort((a, b) => a.year - b.year || a.id.localeCompare(b.id));
}

export function startRound(day: ScheduledDay): RoundState {
  if (day.cards.length !== CARDS_PER_DAY) {
    throw new Error(`day must have ${CARDS_PER_DAY} cards`);
  }
  return {
    day: day.day,
    pool: [...day.cards],
    slots: Array.from({ length: SLOT_COUNT }, () => null),
    lives: MAX_LIVES,
    ended: false,
    outcome: null,
    attempts: 0,
    lastCorrectCount: null,
  };
}

function assertOpen(state: RoundState): void {
  if (state.ended) throw new Error('round has ended');
}

export function boardFull(state: RoundState): boolean {
  return state.slots.every((slot) => slot !== null);
}

/** Place a pool card into an empty slot. */
export function assignSlot(state: RoundState, cardId: string, slotIndex: number): RoundState {
  assertOpen(state);
  if (slotIndex < 0 || slotIndex >= SLOT_COUNT) throw new Error('invalid slot');
  if (state.slots[slotIndex]) throw new Error('slot occupied');
  const idx = state.pool.findIndex((card) => card.id === cardId);
  if (idx < 0) throw new Error('card not in pool');
  const card = state.pool[idx]!;
  const pool = [...state.pool.slice(0, idx), ...state.pool.slice(idx + 1)];
  const slots = [...state.slots];
  slots[slotIndex] = card;
  return { ...state, pool, slots };
}

/** Return a slot card to the pool. */
export function returnToPool(state: RoundState, slotIndex: number): RoundState {
  assertOpen(state);
  if (slotIndex < 0 || slotIndex >= SLOT_COUNT) throw new Error('invalid slot');
  const card = state.slots[slotIndex];
  if (!card) return state;
  const slots = [...state.slots];
  slots[slotIndex] = null;
  return { ...state, pool: [...state.pool, card], slots };
}

/** Move a placed card to another slot (swap if target occupied) or into an empty target. */
export function moveBetweenSlots(state: RoundState, fromIndex: number, toIndex: number): RoundState {
  assertOpen(state);
  if (fromIndex === toIndex) return state;
  if (fromIndex < 0 || fromIndex >= SLOT_COUNT || toIndex < 0 || toIndex >= SLOT_COUNT) {
    throw new Error('invalid slot');
  }
  const from = state.slots[fromIndex];
  if (!from) throw new Error('source slot empty');
  const slots = [...state.slots];
  const to = slots[toIndex];
  slots[toIndex] = from;
  slots[fromIndex] = to ?? null;
  return { ...state, slots };
}

/**
 * Place `cardId` into `slotIndex` in one step.
 * Pool → empty: assign. Pool → occupied: displace occupant to pool, then assign.
 * Slot → slot: move/swap via `moveBetweenSlots`.
 */
export function placeIntoSlot(state: RoundState, cardId: string, slotIndex: number): RoundState {
  assertOpen(state);
  if (slotIndex < 0 || slotIndex >= SLOT_COUNT) throw new Error('invalid slot');

  const fromSlot = state.slots.findIndex((card) => card?.id === cardId);
  if (fromSlot >= 0) {
    return moveBetweenSlots(state, fromSlot, slotIndex);
  }

  if (!state.pool.some((card) => card.id === cardId)) {
    throw new Error('card not found');
  }

  let next = state;
  if (next.slots[slotIndex]) {
    next = returnToPool(next, slotIndex);
  }
  return assignSlot(next, cardId, slotIndex);
}

export function scoreSlots(slots: Array<HistoryEvent | null>): number {
  const filled = slots.filter((slot): slot is HistoryEvent => slot !== null);
  if (filled.length !== SLOT_COUNT) return 0;
  const truth = trueOrder(filled);
  let correct = 0;
  for (let i = 0; i < SLOT_COUNT; i += 1) {
    if (slots[i]?.id === truth[i]?.id) correct += 1;
  }
  return correct;
}

export function submitGuess(state: RoundState): SubmitResult {
  assertOpen(state);
  if (!boardFull(state)) {
    throw new Error('board incomplete');
  }
  const correctCount = scoreSlots(state.slots);
  const attempts = state.attempts + 1;
  const won = correctCount === SLOT_COUNT;

  if (won) {
    return {
      correctCount,
      won: true,
      state: {
        ...state,
        attempts,
        lastCorrectCount: correctCount,
        ended: true,
        outcome: 'win',
        lives: state.lives,
      },
    };
  }

  const lives = state.lives - 1;
  const lost = lives <= 0;
  const pool = state.slots.filter((slot): slot is HistoryEvent => slot !== null);
  return {
    correctCount,
    won: false,
    state: {
      ...state,
      attempts,
      lastCorrectCount: correctCount,
      lives,
      pool,
      slots: Array.from({ length: SLOT_COUNT }, () => null),
      ended: lost,
      outcome: lost ? 'loss' : null,
    },
  };
}
